import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';
import { memo } from 'react';

import { leverageRestrictionBunja } from '@flipster/features/src/margin-bonus/leverage-restriction.state';
import Formatted from '@flipster/react/design-utils/formatted';
import PlusCircleIcon from '@flipster/react/icon/generated/PlusCircleIcon';
import { hstack, stack } from '@flipster/styled-system/patterns';
import { token } from '@flipster/styled-system/tokens';
import { TableCell } from '@flipster/ui/Table';
import { getQuoteCurrencyPrecision } from '@flipster/utils/constants/currency';

import { openPositionRowDataBunja } from '../row-bunja';
import { useOpenPositionRowActions } from '../use-open-position-row-actions';

const QuantitySizeCell = memo(() => {
  const t = useTranslations();
  const { quantityAtom, displaySizeAtom, isAirdropAtom, instrumentAtom } = useBunja(openPositionRowDataBunja);
  const quantity = useAtomValue(quantityAtom);
  const size = useAtomValue(displaySizeAtom);
  const isAirdrop = useAtomValue(isAirdropAtom);
  const instrument = useAtomValue(instrumentAtom);

  const { handleAddPosition } = useOpenPositionRowActions();
  const { leverageRestrictionAtom } = useBunja(leverageRestrictionBunja);
  const { isMaxInitLeverageRestricted } = useAtomValue(leverageRestrictionAtom);

  if (!instrument) return null;

  const { quoteCurrency, tradeQtyPrecision } = instrument;
  const quoteCurrencyPrecision = getQuoteCurrencyPrecision(quoteCurrency);

  return (
    <TableCell>
      {isAirdrop ? (
        <div className={stack({ align: 'start' })}>
          <Formatted.Number
            value={quantity}
            precision={tradeQtyPrecision}
            toFixed
            abs
            css={{ textStyle: 'Text/S/Monospace/Regular', color: 'content.default.level-5' }}
          />
          <Formatted.Number
            value={size}
            toFixed
            abs
            precision={quoteCurrencyPrecision}
            suffix={quoteCurrency}
            css={{ textStyle: 'Text/S/Monospace/Regular', color: 'content.default.level-2' }}
          />
        </div>
      ) : (
        <button
          type='button'
          className={hstack({ gap: 4, _disabled: { '& > svg': { color: 'content.interaction.disabled' } } })}
          onClick={handleAddPosition}
          disabled={isMaxInitLeverageRestricted}
          aria-label={t('Assets.r250401.Add_position')}
        >
          <div className={stack({ align: 'start' })}>
            <Formatted.Number
              value={quantity}
              precision={tradeQtyPrecision}
              toFixed
              abs
              css={{ textStyle: 'Text/S/Monospace/Regular', color: 'content.default.level-5' }}
            />
            <Formatted.Number
              value={size}
              toFixed
              abs
              precision={quoteCurrencyPrecision}
              suffix={quoteCurrency}
              css={{ textStyle: 'Text/S/Monospace/Regular', color: 'content.default.level-2' }}
            />
          </div>
          <PlusCircleIcon size={18} color={token('colors.content.default.level-2')} />
        </button>
      )}
    </TableCell>
  );
});

export default QuantitySizeCell;
